import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calculator, 
  TrendingUp, 
  ScrollText, 
  Landmark,
  BarChart3,
  PieChart,
  ArrowRight,
  Loader2,
  Sparkles,
  Target,
  Clock,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  Shield,
  ChevronRight,
  Info,
  Gift,
  Search
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, AreaChart, ReferenceLine, CartesianGrid } from 'recharts';
import Layout from '../components/Layout';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const investmentTypes = [
  { id: 'stocks', icon: TrendingUp, color: 'from-emerald-500 to-emerald-600', bgColor: 'bg-emerald-500/10', textColor: 'text-emerald-500', risk: 'High', riskSw: 'Juu' },
  { id: 'bonds', icon: ScrollText, color: 'from-blue-500 to-blue-600', bgColor: 'bg-blue-500/10', textColor: 'text-blue-500', risk: 'Low', riskSw: 'Chini' },
  { id: 'treasury_bills', icon: Landmark, color: 'from-purple-500 to-purple-600', bgColor: 'bg-purple-500/10', textColor: 'text-purple-500', risk: 'Very Low', riskSw: 'Chini Sana' },
  { id: 'etfs', icon: BarChart3, color: 'from-orange-500 to-orange-600', bgColor: 'bg-orange-500/10', textColor: 'text-orange-500', risk: 'Medium', riskSw: 'Wastani' },
  { id: 'utt', icon: PieChart, color: 'from-teal-500 to-teal-600', bgColor: 'bg-teal-500/10', textColor: 'text-teal-500', risk: 'Medium', riskSw: 'Wastani' }
];

export default function Simulator() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [scenarios, setScenarios] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [simulatorMode, setSimulatorMode] = useState('category'); // 'category' or 'individual'
  const [individualAssets, setIndividualAssets] = useState({ stocks: [], utt_funds: [], etfs: [] });
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [assetSearchQuery, setAssetSearchQuery] = useState('');
  const [formData, setFormData] = useState({
    investment_type: 'stocks',
    initial_amount: 500000,
    duration_years: 5,
    monthly_contribution: 50000
  });

  const fetchRates = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/simulator/rates`);
      setRates(response.data.rates);
    } catch (error) {
      console.error('Failed to fetch rates:', error);
    }
  }, []);

  const fetchIndividualAssets = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/simulator/assets`);
      setIndividualAssets(response.data);
    } catch (error) {
      console.error('Failed to fetch individual assets:', error);
    }
  }, []);

  useEffect(() => {
    fetchRates();
    fetchIndividualAssets();
  }, [fetchRates, fetchIndividualAssets]);

  // Calculate scenarios and chart data when result changes (ONLY for category mode)
  useEffect(() => {
    // Skip this effect for individual asset mode - that's handled in handleCalculate
    if (simulatorMode === 'individual') {
      return;
    }
    
    if (result && rates[formData.investment_type]) {
      const rate = rates[formData.investment_type].rates;
      const years = formData.duration_years;
      
      // Generate yearly projections for all scenarios
      const projections = [];
      for (let year = 0; year <= years; year++) {
        const months = year * 12;
        const monthlyRateLow = rate.low / 12;
        const monthlyRateAvg = rate.avg / 12;
        const monthlyRateHigh = rate.high / 12;
        
        const calculateFV = (monthlyRate) => {
          const fvInitial = formData.initial_amount * Math.pow(1 + monthlyRate, months);
          const fvContributions = formData.monthly_contribution > 0 
            ? formData.monthly_contribution * ((Math.pow(1 + monthlyRate, months) - 1) / monthlyRate)
            : 0;
          return fvInitial + fvContributions;
        };
        
        projections.push({
          year: year,
          conservative: Math.round(calculateFV(monthlyRateLow)),
          expected: Math.round(calculateFV(monthlyRateAvg)),
          optimistic: Math.round(calculateFV(monthlyRateHigh)),
          contributions: formData.initial_amount + (formData.monthly_contribution * months)
        });
      }
      
      setChartData(projections);
      
      // Set scenario results
      const finalYear = projections[projections.length - 1];
      setScenarios({
        conservative: {
          value: finalYear.conservative,
          returns: finalYear.conservative - finalYear.contributions,
          rate: rate.low * 100
        },
        expected: {
          value: finalYear.expected,
          returns: finalYear.expected - finalYear.contributions,
          rate: rate.avg * 100
        },
        optimistic: {
          value: finalYear.optimistic,
          returns: finalYear.optimistic - finalYear.contributions,
          rate: rate.high * 100
        },
        contributions: finalYear.contributions
      });
    }
  }, [result, rates, formData, simulatorMode]);

  const handleCalculate = async () => {
    if (formData.initial_amount < 20000) {
      toast.error(t('Minimum investment is TZS 20,000', 'Uwekezaji wa chini ni TZS 20,000'));
      return;
    }

    setLoading(true);
    try {
      if (simulatorMode === 'individual' && selectedAsset) {
        // Individual asset calculation
        const response = await axios.post(
          `${API}/simulator/calculate-asset?asset_type=${selectedAsset.type}&asset_id=${selectedAsset.id}&initial_amount=${formData.initial_amount}&monthly_contribution=${formData.monthly_contribution}&years=${formData.duration_years}`
        );
        
        // Transform individual result to match category format
        const data = response.data;
        setResult({
          investment_type: selectedAsset.type,
          initial_amount: formData.initial_amount,
          duration_years: formData.duration_years,
          monthly_contribution: formData.monthly_contribution,
          projected_value: data.projection.expected,
          total_contributions: data.summary.total_contributions,
          total_returns: data.summary.total_returns,
          annual_rate: data.asset.estimated_annual_return
        });
        
        // Set scenarios from individual response
        setScenarios({
          conservative: {
            value: data.projection.conservative,
            returns: data.projection.conservative - data.summary.total_contributions,
            rate: data.asset.estimated_annual_return * 0.85
          },
          expected: {
            value: data.projection.expected,
            returns: data.summary.total_returns,
            rate: data.asset.estimated_annual_return
          },
          optimistic: {
            value: data.projection.optimistic,
            returns: data.projection.optimistic - data.summary.total_contributions,
            rate: data.asset.estimated_annual_return * 1.15
          },
          contributions: data.summary.total_contributions
        });
        
        // Generate chart data from yearly breakdown
        const projections = [{ year: 0, conservative: formData.initial_amount, expected: formData.initial_amount, optimistic: formData.initial_amount, contributions: formData.initial_amount }];
        data.yearly_breakdown.forEach(y => {
          projections.push({
            year: y.year,
            conservative: Math.round(y.value * 0.85),
            expected: y.value,
            optimistic: Math.round(y.value * 1.15),
            contributions: y.invested
          });
        });
        setChartData(projections);
        
      } else {
        // Category calculation (existing logic)
        const response = await axios.post(`${API}/simulator/calculate`, formData);
        setResult(response.data);
      }
    } catch (error) {
      toast.error(t('Failed to calculate', 'Imeshindwa kuhesabu'));
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000000) {
      return `${(amount / 1000000000).toFixed(1)}B`;
    }
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M`;
    }
    if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K`;
    }
    return amount.toLocaleString();
  };

  const formatFullCurrency = (amount) => {
    return new Intl.NumberFormat('en-TZ', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Calculate milestones
  const getMilestones = () => {
    if (!chartData.length || !scenarios) return [];
    const milestones = [];
    const targets = [1000000, 5000000, 10000000, 50000000, 100000000];
    
    targets.forEach(target => {
      const yearReached = chartData.find(d => d.expected >= target);
      if (yearReached && yearReached.year <= formData.duration_years) {
        milestones.push({
          target,
          year: yearReached.year,
          label: `TZS ${formatCurrency(target)}`
        });
      }
    });
    
    return milestones.slice(0, 3); // Show max 3 milestones
  };

  const selectedRate = rates[formData.investment_type];
  const selectedType = investmentTypes.find(t => t.id === formData.investment_type);

  // Custom tooltip for chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-xl p-3 shadow-xl">
          <p className="font-semibold text-foreground mb-2">
            {t('Year', 'Mwaka')} {label}
          </p>
          <div className="space-y-1 text-sm">
            <p className="text-emerald-500">
              {t('Optimistic', 'Imara')}: TZS {formatFullCurrency(payload[2]?.value || 0)}
            </p>
            <p className="text-primary font-semibold">
              {t('Expected', 'Inayotarajiwa')}: TZS {formatFullCurrency(payload[1]?.value || 0)}
            </p>
            <p className="text-amber-500">
              {t('Conservative', 'Salama')}: TZS {formatFullCurrency(payload[0]?.value || 0)}
            </p>
            <p className="text-muted-foreground border-t border-border pt-1 mt-1">
              {t('Invested', 'Umeiweka')}: TZS {formatFullCurrency(payload[0]?.payload?.contributions || 0)}
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <Layout>
      <div className="page-enter space-y-6 pb-8">
        {/* Hero Header */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 p-6 md:p-8">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
          <div className="relative">
            <Badge className="bg-primary/20 text-primary border-0 mb-3">
              <Sparkles className="w-3 h-3 mr-1" />
              {t('AI-Powered Projections', 'Makadirio ya AI')}
            </Badge>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
              {t('Investment Simulator', 'Kihesabu cha Uwekezaji')}
            </h1>
            <p className="text-muted-foreground max-w-xl">
              {t('See exactly how your money can grow. Choose your investment, set your goals, and watch your future wealth unfold.', 
                 'Ona jinsi pesa yako inavyoweza kukua. Chagua uwekezaji wako, weka malengo yako, na utazame utajiri wako wa baadaye.')}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Input Form - 2 columns */}
          <div className="lg:col-span-2 space-y-5">
            {/* Investment Type Cards */}
            <Card className="border-border/50 overflow-hidden">
              <CardHeader className="pb-3 bg-muted/30">
                <CardTitle className="text-base flex items-center gap-2">
                  <Target className="w-4 h-4 text-primary" />
                  {t('Investment Type', 'Aina ya Uwekezaji')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                {/* Mode Toggle */}
                <div className="flex gap-2 p-1 bg-muted/50 rounded-lg mb-4">
                  <button
                    onClick={() => { setSimulatorMode('category'); setSelectedAsset(null); }}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                      simulatorMode === 'category' 
                        ? 'bg-background shadow-sm text-foreground' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('Categories', 'Makundi')}
                  </button>
                  <button
                    onClick={() => setSimulatorMode('individual')}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                      simulatorMode === 'individual' 
                        ? 'bg-background shadow-sm text-foreground' 
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {t('Individual Assets', 'Mali Maalum')}
                  </button>
                </div>

                {simulatorMode === 'category' ? (
                  <>
                    {investmentTypes.map(type => {
                      const rate = rates[type.id];
                      const isSelected = formData.investment_type === type.id;
                      return (
                        <button
                          key={type.id}
                          onClick={() => setFormData({...formData, investment_type: type.id})}
                          className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 ${
                            isSelected 
                              ? 'border-primary bg-primary/5 shadow-md shadow-primary/10' 
                              : 'border-border/50 hover:border-primary/30 hover:bg-muted/30'
                          }`}
                          data-testid={`investment-type-${type.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${type.color} flex items-center justify-center shadow-lg`}>
                              <type.icon className="w-6 h-6 text-white" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <p className="font-semibold text-foreground">
                                  {rate && (language === 'sw' ? rate.name_sw : rate.name_en)}
                                </p>
                                {rate && (
                                  <Badge variant="outline" className={`text-xs ${type.textColor} border-current`}>
                                    {(rate.rates.avg * 100).toFixed(0)}% {t('avg', 'wst')}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <Shield className={`w-3 h-3 ${type.textColor}`} />
                                <span className={`text-xs ${type.textColor}`}>
                                  {t('Risk', 'Hatari')}: {language === 'sw' ? type.riskSw : type.risk}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                    
                    {/* UTT AMIS Link */}
                    {formData.investment_type === 'utt' && (
                      <div className="mt-4 p-4 bg-teal-50 dark:bg-teal-900/20 rounded-xl border border-teal-200 dark:border-teal-800">
                        <p className="text-sm text-teal-700 dark:text-teal-300 mb-2">
                          {t('Want to explore specific UTT AMIS funds?', 'Unataka kuchunguza mifuko maalum ya UTT AMIS?')}
                        </p>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-teal-600 border-teal-300 hover:bg-teal-100"
                          onClick={() => navigate('/utt-amis')}
                        >
                          {t('View UTT AMIS Funds', 'Tazama Mifuko ya UTT AMIS')}
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  /* Individual Assets Selection */
                  <div className="space-y-4">
                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder={t('Search stocks, ETFs, UTT funds...', 'Tafuta hisa, ETF, mifuko ya UTT...')}
                        value={assetSearchQuery}
                        onChange={(e) => setAssetSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                      />
                    </div>

                    {/* Asset Categories */}
                    <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                      {/* Stocks */}
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1 sticky top-0 bg-card py-1">
                          <TrendingUp className="w-3 h-3" /> {t('DSE Stocks', 'Hisa za DSE')} ({individualAssets.stocks?.length || 0})
                        </p>
                        <div className="space-y-1.5">
                          {individualAssets.stocks
                            ?.filter(s => s.name.toLowerCase().includes(assetSearchQuery.toLowerCase()) || s.id.toLowerCase().includes(assetSearchQuery.toLowerCase()))
                            .map(stock => (
                            <button
                              key={stock.id}
                              onClick={() => setSelectedAsset(stock)}
                              className={`w-full p-3 rounded-lg border text-left transition-all text-sm ${
                                selectedAsset?.id === stock.id && selectedAsset?.type === 'stock'
                                  ? 'border-emerald-500 bg-emerald-500/10'
                                  : 'border-border/50 hover:border-emerald-300 hover:bg-muted/30'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-semibold text-foreground">{stock.id}</span>
                                  <span className="text-muted-foreground ml-2">{stock.name}</span>
                                </div>
                                <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-300">
                                  {stock.estimated_annual_return?.toFixed(1)}%
                                </Badge>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* UTT Funds */}
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                          <PieChart className="w-3 h-3" /> {t('UTT Funds', 'Mifuko ya UTT')} ({individualAssets.utt_funds?.length || 0})
                        </p>
                        <div className="space-y-1.5">
                          {individualAssets.utt_funds
                            ?.filter(f => f.name.toLowerCase().includes(assetSearchQuery.toLowerCase()))
                            .map(fund => (
                            <button
                              key={fund.id}
                              onClick={() => setSelectedAsset(fund)}
                              className={`w-full p-3 rounded-lg border text-left transition-all text-sm ${
                                selectedAsset?.id === fund.id && selectedAsset?.type === 'utt'
                                  ? 'border-teal-500 bg-teal-500/10'
                                  : 'border-border/50 hover:border-teal-300 hover:bg-muted/30'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-semibold text-foreground">{fund.name}</span>
                                  <span className="text-muted-foreground ml-2 text-xs">{fund.category}</span>
                                </div>
                                <Badge variant="outline" className="text-xs text-teal-600 border-teal-300">
                                  {fund.estimated_annual_return?.toFixed(1)}%
                                </Badge>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* ETFs */}
                      <div>
                        <p className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                          <BarChart3 className="w-3 h-3" /> {t('ETFs', 'ETFs')} ({individualAssets.etfs?.length || 0})
                        </p>
                        <div className="space-y-1.5">
                          {individualAssets.etfs
                            ?.filter(e => e.name.toLowerCase().includes(assetSearchQuery.toLowerCase()))
                            .map(etf => (
                            <button
                              key={etf.id}
                              onClick={() => setSelectedAsset(etf)}
                              className={`w-full p-3 rounded-lg border text-left transition-all text-sm ${
                                selectedAsset?.id === etf.id && selectedAsset?.type === 'etf'
                                  ? 'border-orange-500 bg-orange-500/10'
                                  : 'border-border/50 hover:border-orange-300 hover:bg-muted/30'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <span className="font-semibold text-foreground">{etf.id}</span>
                                  <span className="text-muted-foreground ml-2 text-xs">{etf.name}</span>
                                </div>
                                <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                                  {etf.estimated_annual_return?.toFixed(1)}%
                                </Badge>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Selected Asset Summary */}
                    {selectedAsset && (
                      <div className="mt-3 p-3 bg-primary/5 rounded-lg border border-primary/20">
                        <p className="text-xs text-muted-foreground mb-1">{t('Selected', 'Umechagua')}:</p>
                        <p className="font-semibold text-foreground">{selectedAsset.name}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs">
                          <span className="text-primary">
                            {t('Est. Return', 'Faida')}: {selectedAsset.estimated_annual_return?.toFixed(1)}%
                          </span>
                          <span className="text-muted-foreground">
                            {t('Risk', 'Hatari')}: {selectedAsset.risk_level}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Amount & Duration */}
            <Card className="border-border/50">
              <CardHeader className="pb-3 bg-muted/30">
                <CardTitle className="text-base flex items-center gap-2">
                  <Wallet className="w-4 h-4 text-primary" />
                  {t('Investment Details', 'Maelezo ya Uwekezaji')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-5 space-y-6">
                {/* Initial Amount */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-foreground font-medium">
                      {t('Starting Amount', 'Kiasi cha Kuanzia')}
                    </Label>
                    <span className="text-2xl font-bold text-primary">
                      TZS {formatCurrency(formData.initial_amount)}
                    </span>
                  </div>
                  <Slider
                    value={[formData.initial_amount]}
                    onValueChange={(value) => setFormData({...formData, initial_amount: value[0]})}
                    min={20000}
                    max={50000000}
                    step={10000}
                    className="py-4"
                    data-testid="initial-amount-slider"
                  />
                  <div className="flex gap-2 flex-wrap">
                    {[100000, 500000, 1000000, 5000000, 10000000].map(amount => (
                      <Button
                        key={amount}
                        variant={formData.initial_amount === amount ? "default" : "outline"}
                        size="sm"
                        onClick={() => setFormData({...formData, initial_amount: amount})}
                        className="text-xs rounded-full"
                      >
                        {formatCurrency(amount)}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Duration */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-foreground font-medium flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      {t('Investment Period', 'Kipindi')}
                    </Label>
                    <span className="text-2xl font-bold text-foreground">
                      {formData.duration_years} <span className="text-sm font-normal text-muted-foreground">{t('years', 'miaka')}</span>
                    </span>
                  </div>
                  <Slider
                    value={[formData.duration_years]}
                    onValueChange={(value) => setFormData({...formData, duration_years: value[0]})}
                    min={1}
                    max={30}
                    step={1}
                    className="py-4"
                    data-testid="duration-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>1 {t('year', 'mwaka')}</span>
                    <span className="text-primary font-medium">
                      {formData.duration_years <= 3 ? t('Short-term', 'Mfupi') : 
                       formData.duration_years <= 10 ? t('Medium-term', 'Wastani') : 
                       t('Long-term', 'Mrefu')}
                    </span>
                    <span>30 {t('years', 'miaka')}</span>
                  </div>
                </div>

                {/* Monthly Contribution */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-foreground font-medium flex items-center gap-2">
                      <Gift className="w-4 h-4 text-muted-foreground" />
                      {t('Monthly Addition', 'Nyongeza ya Kila Mwezi')}
                    </Label>
                    <span className="text-lg font-semibold text-foreground">
                      TZS {formatCurrency(formData.monthly_contribution)}
                    </span>
                  </div>
                  <Slider
                    value={[formData.monthly_contribution]}
                    onValueChange={(value) => setFormData({...formData, monthly_contribution: value[0]})}
                    min={0}
                    max={1000000}
                    step={10000}
                    className="py-4"
                    data-testid="monthly-slider"
                  />
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    {t('Adding regularly supercharges your growth', 'Kuongeza mara kwa mara kunakuza ukuaji wako')}
                  </p>
                </div>

                <Button 
                  onClick={handleCalculate}
                  className="w-full h-14 rounded-2xl text-base font-semibold bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/20"
                  disabled={loading || (simulatorMode === 'individual' && !selectedAsset)}
                  data-testid="calculate-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {t('Calculating...', 'Inahesabu...')}
                    </>
                  ) : (
                    <>
                      <Zap className="w-5 h-5 mr-2" />
                      {simulatorMode === 'individual' && selectedAsset 
                        ? `${t('Calculate for', 'Hesabu kwa')} ${selectedAsset.id}`
                        : t('Calculate My Future', 'Hesabu Mustakabali Wangu')}
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Results - 3 columns */}
          <div className="lg:col-span-3 space-y-5">
            {result && scenarios ? (
              <>
                {/* Main Result Card */}
                <Card className="border-0 bg-gradient-to-br from-primary/20 via-primary/10 to-transparent overflow-hidden">
                  <CardContent className="p-6 md:p-8">
                    <div className="flex items-start justify-between mb-6">
                      <div>
                        <p className="text-sm text-muted-foreground mb-1">
                          {t('Your wealth after', 'Utajiri wako baada ya')} {result.duration_years} {t('years', 'miaka')}
                        </p>
                        <p className="text-4xl md:text-5xl font-bold text-foreground" data-testid="projected-value">
                          TZS {formatCurrency(scenarios.expected.value)}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className="bg-green-500/20 text-green-500 border-0">
                            <ArrowUpRight className="w-3 h-3 mr-1" />
                            +{((scenarios.expected.returns / scenarios.contributions) * 100).toFixed(0)}%
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {t('return on investment', 'faida ya uwekezaji')}
                          </span>
                        </div>
                      </div>
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${selectedType?.color} flex items-center justify-center shadow-xl`}>
                        {selectedType && <selectedType.icon className="w-8 h-8 text-white" />}
                      </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-background/60 backdrop-blur-sm rounded-xl p-4">
                        <p className="text-xs text-muted-foreground mb-1">{t('You Invest', 'Unaiweka')}</p>
                        <p className="text-lg font-bold text-foreground">TZS {formatCurrency(scenarios.contributions)}</p>
                      </div>
                      <div className="bg-background/60 backdrop-blur-sm rounded-xl p-4">
                        <p className="text-xs text-muted-foreground mb-1">{t('You Earn', 'Unapata')}</p>
                        <p className="text-lg font-bold text-green-500">+TZS {formatCurrency(scenarios.expected.returns)}</p>
                      </div>
                      <div className="bg-background/60 backdrop-blur-sm rounded-xl p-4">
                        <p className="text-xs text-muted-foreground mb-1">{t('Annual Rate', 'Kiwango/Mwaka')}</p>
                        <p className="text-lg font-bold text-primary">{scenarios.expected.rate.toFixed(0)}%</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Growth Chart */}
                <Card className="border-border/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-primary" />
                        {t('Wealth Growth Over Time', 'Ukuaji wa Utajiri')}
                      </span>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> {t('Optimistic', 'Imara')}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-primary" /> {t('Expected', 'Tarajiwa')}</span>
                        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> {t('Conservative', 'Salama')}</span>
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="h-64 md:h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="colorOptimistic" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                            </linearGradient>
                            <linearGradient id="colorExpected" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.4}/>
                              <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.5} />
                          <XAxis 
                            dataKey="year" 
                            tick={{ fontSize: 12 }} 
                            stroke="hsl(var(--muted-foreground))"
                            tickFormatter={(value) => `${value}y`}
                          />
                          <YAxis 
                            tick={{ fontSize: 12 }} 
                            stroke="hsl(var(--muted-foreground))"
                            tickFormatter={(value) => formatCurrency(value)}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <ReferenceLine 
                            y={scenarios.contributions} 
                            stroke="hsl(var(--muted-foreground))" 
                            strokeDasharray="5 5"
                            label={{ value: t('Invested', 'Ulioweka'), position: 'right', fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
                          />
                          <Area type="monotone" dataKey="conservative" stroke="#f59e0b" strokeWidth={2} fill="transparent" />
                          <Area type="monotone" dataKey="expected" stroke="hsl(var(--primary))" strokeWidth={3} fill="url(#colorExpected)" />
                          <Area type="monotone" dataKey="optimistic" stroke="#10b981" strokeWidth={2} fill="url(#colorOptimistic)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>

                {/* Scenarios Comparison */}
                <div className="grid md:grid-cols-3 gap-4">
                  {/* Conservative */}
                  <Card className="border-amber-500/30 bg-amber-500/5">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Shield className="w-5 h-5 text-amber-500" />
                        <span className="font-semibold text-foreground">{t('Conservative', 'Salama')}</span>
                      </div>
                      <p className="text-2xl font-bold text-foreground mb-1">
                        TZS {formatCurrency(scenarios.conservative.value)}
                      </p>
                      <p className="text-sm text-amber-500">
                        +TZS {formatCurrency(scenarios.conservative.returns)} ({scenarios.conservative.rate.toFixed(0)}%/yr)
                      </p>
                    </CardContent>
                  </Card>
                  
                  {/* Expected */}
                  <Card className="border-primary/30 bg-primary/5 ring-2 ring-primary/20">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Target className="w-5 h-5 text-primary" />
                        <span className="font-semibold text-foreground">{t('Expected', 'Inayotarajiwa')}</span>
                        <Badge className="bg-primary/20 text-primary text-xs border-0">{t('Most Likely', 'Inayowezekana')}</Badge>
                      </div>
                      <p className="text-2xl font-bold text-foreground mb-1">
                        TZS {formatCurrency(scenarios.expected.value)}
                      </p>
                      <p className="text-sm text-primary">
                        +TZS {formatCurrency(scenarios.expected.returns)} ({scenarios.expected.rate.toFixed(0)}%/yr)
                      </p>
                    </CardContent>
                  </Card>
                  
                  {/* Optimistic */}
                  <Card className="border-emerald-500/30 bg-emerald-500/5">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-5 h-5 text-emerald-500" />
                        <span className="font-semibold text-foreground">{t('Optimistic', 'Imara')}</span>
                      </div>
                      <p className="text-2xl font-bold text-foreground mb-1">
                        TZS {formatCurrency(scenarios.optimistic.value)}
                      </p>
                      <p className="text-sm text-emerald-500">
                        +TZS {formatCurrency(scenarios.optimistic.returns)} ({scenarios.optimistic.rate.toFixed(0)}%/yr)
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Milestones */}
                {getMilestones().length > 0 && (
                  <Card className="border-border/50 bg-muted/30">
                    <CardContent className="p-5">
                      <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
                        <Gift className="w-4 h-4 text-primary" />
                        {t('Wealth Milestones', 'Malengo ya Utajiri')}
                      </h3>
                      <div className="flex flex-wrap gap-3">
                        {getMilestones().map((milestone, i) => (
                          <Badge key={i} variant="outline" className="py-2 px-4 text-sm">
                            <Target className="w-3 h-3 mr-2 text-primary" />
                            {milestone.label} {t('in', 'katika')} {milestone.year} {t('years', 'miaka')}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    onClick={() => navigate('/portfolio/add')}
                    className="flex-1 h-12 rounded-xl"
                    data-testid="start-investing-btn"
                  >
                    {t('Start Investing Now', 'Anza Kuwekeza Sasa')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/quiz')}
                    className="flex-1 h-12 rounded-xl"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    {t('Check My Risk Profile', 'Angalia Wasifu Wangu')}
                  </Button>
                </div>
              </>
            ) : (
              <Card className="border-dashed border-2 h-full flex items-center justify-center min-h-[500px]">
                <CardContent className="text-center py-12 px-6">
                  <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mx-auto mb-6">
                    <Calculator className="w-12 h-12 text-primary/50" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    {t('See Your Financial Future', 'Ona Mustakabali Wako wa Kifedha')}
                  </h3>
                  <p className="text-muted-foreground max-w-sm mx-auto mb-6">
                    {t('Configure your investment on the left and click calculate to visualize your wealth growth journey.', 
                       'Sanidi uwekezaji wako upande wa kushoto na bonyeza hesabu kuona safari ya ukuaji wa utajiri wako.')}
                  </p>
                  <div className="flex flex-wrap justify-center gap-2">
                    <Badge variant="outline" className="py-1.5">
                      <TrendingUp className="w-3 h-3 mr-1" /> {t('3 Scenarios', 'Hali 3')}
                    </Badge>
                    <Badge variant="outline" className="py-1.5">
                      <BarChart3 className="w-3 h-3 mr-1" /> {t('Visual Charts', 'Chati')}
                    </Badge>
                    <Badge variant="outline" className="py-1.5">
                      <Target className="w-3 h-3 mr-1" /> {t('Milestones', 'Malengo')}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Disclaimer */}
            <Card className="bg-muted/30 border-0">
              <CardContent className="p-4 flex items-start gap-3">
                <Info className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {t(
                    'These projections are based on historical DSE performance and are for educational purposes only. Actual returns may vary significantly. Past performance does not guarantee future results. Consider consulting a licensed financial advisor.',
                    'Makadirio haya yanategemea utendaji wa kihistoria wa DSE na ni kwa madhumuni ya elimu tu. Faida halisi zinaweza kutofautiana sana. Utendaji wa zamani hauhakikishi matokeo ya baadaye. Fikiria kushauriana na mshauri wa kifedha aliyesajiliwa.'
                  )}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
